import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ModalBackground from "../../components/common/ModalBackground";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import { generateNoDataValues, getFiltersInfo } from "../../common/utils";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import {
  getClearResolutionModel,
  getResolutionsFilteringOptions,
} from "../../common/EntitiesManagement";
import AddOrEditResolution from "./components/AddOrEditResolution";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function Resolutions() {
  const {
    resolutionModel,
    setResolutionModel,
    resolutionData,
    RESOLUTION_ENDPOINT,
    config,
    pageIndexResolution,
    setPageIndexResolution,
    toggleVisibleModalConfirm,
    deleteEntity,
    createEntity,
    updateEntity,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();
  const { id } = useParams();
  const [resolutionDataFiltered, setResolutionFiltered] = useState(
    resolutionData.Data
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getResolutionsFilteringOptions());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const resolutionDataObj = resolutionData.Data.find(
        (resolutionObj) => resolutionObj.Id === Number(id)
      );
      setResolutionModel(resolutionDataObj);
    }
  }, [id]);

  useEffect(() => {
    setResolutionFiltered(resolutionData.Data);
  }, [resolutionData]);

  const validateOperationResult = (result, isDelete) => {
    setResolutionModel(getClearResolutionModel());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([RESOLUTION_ENDPOINT, config]);
    if (isDelete) {
      if (pageIndexResolution !== 0 && resolutionData.Data.length === 1) {
        setPageIndexResolution(pageIndexResolution - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${RESOLUTION_ENDPOINT}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${RESOLUTION_ENDPOINT}/${data.Id}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${RESOLUTION_ENDPOINT}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const disableSavechanges = () =>
    !resolutionModel?.Name ||
    resolutionModel?.Name.replaceAll(" ", "").length <= 0 ||
    !resolutionModel?.Code ||
    resolutionModel?.Code.replaceAll(" ", "").length <= 0;

  const getColumnTitles = () => {
    return [
      localize.code,
      localize.name,
      localize.description,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.resolutions}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={RESOLUTION_ENDPOINT}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="resolution"
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_resolution}
          isEditForm
          onClickToSubmit={() => {
            handleUpdateRequest(resolutionModel);
            setShowEditModal(false);
            setResolutionModel(getClearResolutionModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setResolutionModel(getClearResolutionModel());
          }}
          Component={
            <AddOrEditResolution
              model={resolutionModel}
              setModel={setResolutionModel}
            />
          }
          validators={disableSavechanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.resolution}
            subtitle={localize.resolution_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => {
              setShowPrinterModal(true);
            }}
            entityName="resolution_filter"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_resolution}
                onClickToSubmit={() => {
                  handleCreateRequest(resolutionModel);
                  setShowAddModal(false);
                  setResolutionModel(getClearResolutionModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setResolutionModel(getClearResolutionModel());
                }}
                Component={
                  <AddOrEditResolution
                    model={resolutionModel}
                    setModel={setResolutionModel}
                  />
                }
                validators={disableSavechanges()}
              />
            }
          />
        </CardContent>
      </Card>

      {showDeleteModal && (
        <DeleteConfirmationMessage
          entityName={resolutionModel.Code}
          entityType={localize.code}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setShowDeleteModal(false);
            setResolutionModel(getClearResolutionModel());
          }}
          onClickForConfirm={() => {
            handleDeleteRequest(resolutionModel.Id);
            setShowDeleteModal(false);
            setResolutionModel(getClearResolutionModel());
          }}
        />
      )}

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexResolution}
            setPageIndex={setPageIndexResolution}
            dataArray={resolutionData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          resolutionData.Data?.length !== 0
            ? resolutionDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setResolutionModel(item);
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setResolutionModel(item);
                        setShowDeleteModal(true);
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  item.Code,
                  item.Name,
                  <ValueOrNoDataLabel data={item.Description} />,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(6)]
        }
      />
    </div>
  );
}
